const getSectionGap = () => {
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--site-section-gap');
  const gap = Number.parseFloat(value);

  return Number.isFinite(gap) ? gap : 14;
};

const getNavigationBottom = viewportTop => {
  const navigation = document.querySelector('.nav');
  const navigationBottom = navigation?.getBoundingClientRect().bottom;

  if (Number.isFinite(navigationBottom) && navigationBottom > viewportTop) {
    return navigationBottom;
  }

  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--site-nav-height');
  const navigationHeight = Number.parseFloat(value);

  return viewportTop + (Number.isFinite(navigationHeight) ? navigationHeight : 69);
};

const COLLAPSE_TRANSITION_DURATION = 240;
const EXPAND_TRANSITION_DURATION = 240;
let alignmentFrameId = null;
let alignmentTimerId = null;

const cancelAlignment = () => {
  window.cancelAnimationFrame(alignmentFrameId);
  window.clearTimeout(alignmentTimerId);
  alignmentFrameId = null;
  alignmentTimerId = null;
  document.documentElement.classList.remove('is-accordion-collapsing');
};

const measureCollapsedHeight = (section, sectionRect) => {
  const clone = section.cloneNode(true);

  clone.classList.remove('is-open', 'is-closing');
  clone
    .querySelectorAll('.is-open, .is-closing')
    .forEach(element => element.classList.remove('is-open', 'is-closing'));
  clone.classList.add('is-collapse-measurement');
  clone
    .querySelectorAll('[aria-expanded="true"]')
    .forEach(element => element.setAttribute('aria-expanded', 'false'));
  Object.assign(clone.style, {
    position: 'fixed',
    top: '0',
    left: '-10000px',
    width: `${sectionRect.width}px`,
    height: 'auto',
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'none',
    animation: 'none',
  });

  const measurementHost = section.parentElement || document.body;

  measurementHost.append(clone);
  const collapsedHeight = clone.getBoundingClientRect().height;
  clone.remove();

  return collapsedHeight;
};

export const scrollSectionAfterCollapse = section => {
  if (!section) return null;

  const sectionToggle = section.querySelector('[aria-expanded="true"]');
  const documentRoot = document.documentElement;
  const initialSectionRect = section.getBoundingClientRect();
  const viewportTop = window.visualViewport?.offsetTop || 0;
  const viewportHeight = window.visualViewport?.height || window.innerHeight;
  const viewportBottom = viewportTop + viewportHeight;
  const gap = getSectionGap();
  const contentTop = getNavigationBottom(viewportTop) + gap;
  const isFullyVisible =
    initialSectionRect.top >= contentTop &&
    initialSectionRect.bottom <= viewportBottom;
  const isClippedAtBottom = initialSectionRect.bottom > viewportBottom;
  const visibleHeight = Math.max(
    0,
    Math.min(initialSectionRect.bottom, viewportBottom) -
      Math.max(initialSectionRect.top, contentTop)
  );
  let targetViewportTop = contentTop;

  if (isFullyVisible) {
    targetViewportTop = initialSectionRect.top;
  } else if (
    isClippedAtBottom &&
    initialSectionRect.top >= contentTop
  ) {
    const collapsedHeight = measureCollapsedHeight(
      section,
      initialSectionRect
    );

    targetViewportTop =
      visibleHeight >= collapsedHeight
        ? initialSectionRect.top
        : viewportBottom - gap - collapsedHeight;
  }

  const alignSection = () => {
    if (!section.isConnected) return;

    const sectionRect = section.getBoundingClientRect();
    const targetTop =
      window.scrollY + sectionRect.top - targetViewportTop;

    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
  };

  cancelAlignment();
  documentRoot.classList.add('is-accordion-collapsing');

  sectionToggle?.focus({ preventScroll: true });
  alignSection();

  alignmentFrameId = window.requestAnimationFrame(() => {
    alignSection();
    alignmentTimerId = window.setTimeout(() => {
      alignSection();
      documentRoot.classList.remove('is-accordion-collapsing');
      alignmentFrameId = null;
      alignmentTimerId = null;
    }, COLLAPSE_TRANSITION_DURATION);
  });

  return null;
};

export const keepSectionTopDuringExpand = section => {
  if (!section) return null;

  const initialTop = section.getBoundingClientRect().top;
  const viewportTop = window.visualViewport?.offsetTop || 0;
  const contentTop = getNavigationBottom(viewportTop) + getSectionGap();
  const targetViewportTop = Math.max(initialTop, contentTop);
  const startedAt = window.performance.now();

  cancelAlignment();

  const alignSection = () => {
    if (!section.isConnected) return;

    const currentTop = section.getBoundingClientRect().top;
    const targetTop = window.scrollY + currentTop - targetViewportTop;

    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
  };

  const trackExpansion = timestamp => {
    alignSection();

    if (timestamp - startedAt < EXPAND_TRANSITION_DURATION) {
      alignmentFrameId = window.requestAnimationFrame(trackExpansion);
      return;
    }

    alignmentFrameId = null;
  };

  alignmentFrameId = window.requestAnimationFrame(trackExpansion);
  alignmentTimerId = window.setTimeout(() => {
    window.cancelAnimationFrame(alignmentFrameId);
    alignSection();
    alignmentFrameId = null;
    alignmentTimerId = null;
  }, EXPAND_TRANSITION_DURATION + 40);

  return null;
};
